import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Card, Form, Button, Alert, Spinner, Modal } from 'react-bootstrap';
import { SyncButton } from '../../components/SyncButton';
import {
  getIntegrationsSling,
  postIntegrationsSling,
  deleteIntegrationsSlingIntegrationId,
  postIntegrationsSlingIntegrationIdTestConnection,
  postIntegrationsSlingIntegrationIdSync,
} from '../../api/generated/arrayscapeGamingDashboardAPI';
import { useNotifications } from '../../contexts/NotificationContext';

const SlingTab: React.FC = () => {
  const [slingIntegrations, setSlingIntegrations] = useState<any[]>([]);
  const [isLoadingSlingIntegrations, setIsLoadingSlingIntegrations] = useState(false);
  const [slingError, setSlingError] = useState<string | null>(null);
  const [showAddSlingIntegration, setShowAddSlingIntegration] = useState(false);
  const [newSlingName, setNewSlingName] = useState('');
  const [newSlingEmail, setNewSlingEmail] = useState('');
  const [newSlingPassword, setNewSlingPassword] = useState('');
  const [newSlingStartDate, setNewSlingStartDate] = useState('');
  const [newSlingEndDate, setNewSlingEndDate] = useState('');
  const [showSlingPassword, setShowSlingPassword] = useState(false);
  const [isCreatingSling, setIsCreatingSling] = useState(false);

  // Get active syncs to monitor completion
  const { activeSyncs } = useNotifications();

  // Ref to track previous sync states
  const previousSyncStatesRef = useRef<Map<string, string>>(new Map());

  // Fetch integrations on mount
  useEffect(() => {
    fetchSlingIntegrations();
  }, []);

  // Monitor sync completion and refetch integration data
  useEffect(() => {
    activeSyncs.forEach((syncEvent, syncId) => {
      const prevStatus = previousSyncStatesRef.current.get(syncId);
      const currentStatus = syncEvent.status;

      if ((prevStatus === 'started' || prevStatus === 'progress') &&
          (currentStatus === 'completed' || currentStatus === 'failed')) {

        const integrationId = syncEvent.integration_id;

        if (slingIntegrations.some(int => int.id === integrationId)) {
          console.log(`[SlingTab] Sling sync ${currentStatus} for ${integrationId}, refetching integrations`);
          fetchSlingIntegrations();
        }
      }

      previousSyncStatesRef.current.set(syncId, currentStatus);
    });
  }, [activeSyncs, slingIntegrations]);

  const fetchSlingIntegrations = async () => {
    setIsLoadingSlingIntegrations(true);
    setSlingError(null);
    try {
      const response = await getIntegrationsSling();
      setSlingIntegrations(response.integrations || []);
    } catch (error) {
      console.error('Error fetching Sling integrations:', error);
      setSlingError('Failed to fetch Sling integrations');
    } finally {
      setIsLoadingSlingIntegrations(false);
    }
  };

  const handleCreateSlingIntegration = async () => {
    setIsCreatingSling(true);
    setSlingError(null);
    try {
      if (!newSlingName.trim()) {
        setSlingError('Please enter a name for this integration');
        setIsCreatingSling(false);
        return;
      }
      if (!newSlingEmail.trim()) {
        setSlingError('Please enter an email');
        setIsCreatingSling(false);
        return;
      }
      if (!newSlingPassword.trim()) {
        setSlingError('Please enter a password');
        setIsCreatingSling(false);
        return;
      }
      if (!newSlingStartDate || !newSlingEndDate) {
        setSlingError('Please enter both start and end dates');
        setIsCreatingSling(false);
        return;
      }

      await postIntegrationsSling({
        name: newSlingName,
        email: newSlingEmail,
        password: newSlingPassword,
        start_date: new Date(newSlingStartDate).toISOString(),
        end_date: new Date(newSlingEndDate).toISOString()
      });

      setShowAddSlingIntegration(false);
      setNewSlingName('');
      setNewSlingEmail('');
      setNewSlingPassword('');
      setNewSlingStartDate('');
      setNewSlingEndDate('');
      await fetchSlingIntegrations();
    } catch (error: any) {
      console.error('Error creating Sling integration:', error);
      if (error.response?.status === 400) {
        setSlingError('Invalid credentials or name already exists');
      } else {
        setSlingError('Failed to create Sling integration');
      }
    } finally {
      setIsCreatingSling(false);
    }
  };

  const handleDeleteSlingIntegration = async (integrationId: string, name: string) => {
    if (!window.confirm(`Are you sure you want to delete the Sling integration "${name}"?`)) {
      return;
    }

    try {
      await deleteIntegrationsSlingIntegrationId(integrationId);
      await fetchSlingIntegrations();
    } catch (error) {
      console.error('Error deleting Sling integration:', error);
      setSlingError('Failed to delete Sling integration');
    }
  };

  const handleTestSlingConnection = async (integrationId: string, name: string) => {
    try {
      await postIntegrationsSlingIntegrationIdTestConnection(integrationId);
      await fetchSlingIntegrations();
    } catch (error: any) {
      console.error('Error testing Sling connection:', error);
      await fetchSlingIntegrations();
    }
  };

  const handleSyncSlingIntegration = async (integrationId: string, name: string): Promise<string | undefined> => {
    try {
      const resp = await postIntegrationsSlingIntegrationIdSync(integrationId);
      await fetchSlingIntegrations();
      return (resp as any)?.sync_id;
    } catch (error: any) {
      console.error('Error triggering Sling sync:', error);
      await fetchSlingIntegrations();
      return undefined;
    }
  };

  return (
    <>
      <Card className="">
        <Card.Body>
          <div className="d-flex align-items-center justify-content-between mb-4">
            <div className="d-flex align-items-center">
              <div className="ms-4 me-4 bg-header-control rounded p-2">
                <img
                  src="/getsling-logo-transparent.png"
                  alt="GetSling"
                  style={{ height: '4rem' }}
                />
              </div>
              <div>
                <h5 className="mb-1">Sling Integrations</h5>
                <p className="text-muted mb-0">Connect one or more Sling accounts for employee scheduling and time tracking</p>
              </div>
            </div>
            <Button
              variant="primary"
              onClick={() => setShowAddSlingIntegration(true)}
              disabled={isCreatingSling}
            >
              <i className="bi bi-plus-circle me-1"></i>
              Add Integration
            </Button>
          </div>

          {slingError && !showAddSlingIntegration && (
            <Alert variant="danger" dismissible onClose={() => setSlingError(null)}>
              {slingError}
            </Alert>
          )}

          {isLoadingSlingIntegrations ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <p className="mt-2 text-muted">Loading integrations...</p>
            </div>
          ) : slingIntegrations.length === 0 ? (
            <div className="text-center py-5">
              <div className="d-inline-block bg-header-control rounded p-2">
                <img
                  src="/getsling-logo-transparent.png"
                  alt="GetSling"
                  style={{ height: '4rem' }}
                />
              </div>
              <h6 className="mt-3 text-muted">No Sling Integrations</h6>
              <p className="text-muted">Add your first Sling integration to get started</p>
            </div>
          ) : (
            <div className="row">
              {slingIntegrations.map((integration) => (
                <div key={integration.id} className="col-md-6 mb-3">
                  <Card className="border">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div className="flex-grow-1">
                          <h6 className="mb-1">
                            {integration.name || 'Sling Account'}
                          </h6>
                          <div className="text-muted small mb-1">
                            <i className="bi bi-envelope me-1"></i>
                            {integration.email}
                          </div>
                          <div className="text-muted small mb-1">
                            <i className="bi bi-calendar-range me-1"></i>
                            {integration.start_date ? new Date(integration.start_date).toLocaleDateString() : 'Unknown'} - {integration.end_date ? new Date(integration.end_date).toLocaleDateString() : 'Unknown'}
                          </div>
                        </div>
                        <span className={`badge ${
                          integration.connection_status === 'connected' ? 'bg-success' :
                          integration.connection_status === 'error' ? 'bg-danger' : 'bg-secondary'
                        }`}>
                          {integration.connection_status === 'connected' ? 'Connected' :
                           integration.connection_status === 'error' ? 'Error' : 'Not Tested'}
                        </span>
                      </div>

                      <div className="d-flex gap-2 mb-3">
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={() => handleTestSlingConnection(integration.id, integration.name)}
                        >
                          <i className="bi bi-check-circle me-1"></i>
                          Test Connection
                        </Button>
                        <SyncButton
                          claimId={`sling-sync-${integration.id}`}
                          onClick={() => handleSyncSlingIntegration(integration.id, integration.name)}
                        />
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteSlingIntegration(integration.id, integration.name)}
                        >
                          <i className="bi bi-trash me-1"></i>
                          Delete
                        </Button>
                      </div>

                      {integration.connection_message && (
                        <div className={`alert ${integration.connection_status === 'connected' ? 'alert-success' : 'alert-danger'} alert-sm mb-3`} role="alert">
                          <i className={`bi ${integration.connection_status === 'connected' ? 'bi-check-circle' : 'bi-exclamation-triangle'} me-1`}></i>
                          <small>{integration.connection_message}</small>
                        </div>
                      )}

                      <div className="mb-3 pt-2 border-top">
                        <small className="text-muted d-block">Last Sync:</small>
                        <small>
                          {integration.last_sync_at
                            ? new Date(integration.last_sync_at).toLocaleString()
                            : 'Never'
                          }
                        </small>
                      </div>

                      {integration.last_test_at && (
                        <div className="mb-0">
                          <small className="text-muted d-block">Last Test:</small>
                          <small>
                            {new Date(integration.last_test_at).toLocaleString()}
                          </small>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </div>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Add Sling Integration Modal */}
      <Modal show={showAddSlingIntegration} onHide={() => {
        setShowAddSlingIntegration(false);
        setNewSlingName('');
        setNewSlingEmail('');
        setNewSlingPassword('');
        setNewSlingStartDate('');
        setNewSlingEndDate('');
        setSlingError(null);
        setShowSlingPassword(false);
      }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add New Sling Integration</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {slingError && (
            <Alert variant="danger" dismissible onClose={() => setSlingError(null)}>
              {slingError}
            </Alert>
          )}
          <Form.Group className="mb-3">
            <Form.Label>Integration Name <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g., Main Sling Account"
              value={newSlingName}
              onChange={(e) => setNewSlingName(e.target.value)}
              maxLength={100}
            />
            <Form.Text className="text-muted">
              Friendly name to identify this integration
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Email Address <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="email"
              placeholder="Enter your Sling email"
              value={newSlingEmail}
              onChange={(e) => setNewSlingEmail(e.target.value)}
            />
            <Form.Text className="text-muted">
              Your Sling account email address
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Password <span className="text-danger">*</span></Form.Label>
            <div className="input-group">
              <Form.Control
                type={showSlingPassword ? "text" : "password"}
                placeholder="Enter your Sling password"
                value={newSlingPassword}
                onChange={(e) => setNewSlingPassword(e.target.value)}
              />
              <Button
                variant="outline-secondary"
                onClick={() => setShowSlingPassword(!showSlingPassword)}
              >
                <i className={`bi ${showSlingPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
              </Button>
            </div>
            <Form.Text className="text-muted">
              <i className="bi bi-shield-lock me-1"></i>
              Your password will be encrypted and stored securely
            </Form.Text>
          </Form.Group>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Start Date <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="date"
                  value={newSlingStartDate}
                  onChange={(e) => setNewSlingStartDate(e.target.value)}
                />
                <Form.Text className="text-muted">
                  Date range start for syncing data
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>End Date <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="date"
                  value={newSlingEndDate}
                  onChange={(e) => setNewSlingEndDate(e.target.value)}
                />
                <Form.Text className="text-muted">
                  Date range end for syncing data
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => {
              setShowAddSlingIntegration(false);
              setNewSlingName('');
              setNewSlingEmail('');
              setNewSlingPassword('');
              setNewSlingStartDate('');
              setNewSlingEndDate('');
              setSlingError(null);
              setShowSlingPassword(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleCreateSlingIntegration}
            disabled={isCreatingSling || !newSlingName.trim() || !newSlingEmail.trim() || !newSlingPassword.trim() || !newSlingStartDate || !newSlingEndDate}
          >
            {isCreatingSling ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Creating...
              </>
            ) : (
              <>
                <i className="bi bi-plus-circle me-1"></i>
                Create Integration
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SlingTab;
